import i18next from 'i18next';

type ObjectAcceptType = Record<string, any> & { id?: string };
export const isStringArray = (array: unknown[]): array is string[] =>
  Array.isArray(array) && typeof array[0] === 'string';
export const isObjectArray = (array: unknown[]): array is ObjectAcceptType[] =>
  Array.isArray(array) &&
  typeof array[0] === 'object' &&
  array[0] !== null &&
  !Array.isArray(array[0]); //prevent typeof null === 'object'
const isKeyValueArray = (
  array: unknown[],
): array is [string, string | boolean][] =>
  Array.isArray(array) &&
  Array.isArray(array[0]) &&
  array[0].length === 2 &&
  typeof array[0][0] === 'string' &&
  (typeof array[0][1] === 'string' || typeof array[0][1] === 'boolean');

const getTranslatedLabel = (label: string, translationPath?: string) => {
  if (!translationPath) return label;
  const key = `${translationPath}.${label}`;
  return i18next.exists(key) ? i18next.t(key) : label;
};

/* -------------------------------------------------------------------------- */
/* This Function is used for generating options for select-input components   */
/* ---------------- Just apply for transform have label-value --------------- */
/* -------------------------------------------------------------------------- */
type ExtraType<K> = {
  fieldAtLabel?: keyof K;
  translationPath?: string;
  allowedKeyEntryAsValue?: boolean;
};
export const generateOptions = <
  T extends string | ObjectAcceptType | [string, string],
>(
  array: T[],
  extra?: T extends string ? ExtraType<never> : ExtraType<Extract<T, object>>,
) => {
  const {
    translationPath,
    fieldAtLabel = 'name',
    allowedKeyEntryAsValue = false,
  } = extra ?? {};

  if (!Array.isArray(array) || array.length === 0) return [];
  //Whether the array is string array
  if (isStringArray(array)) {
    return (array as string[]).map(item => {
      return {
        label: getTranslatedLabel(item, translationPath),
        value: item,
      };
    });
  }
  //Whether the array is object array
  if (isObjectArray(array)) {
    const hasIdKey = 'id' in array[0];
    return (array as ObjectAcceptType[]).map(objItem => {
      const labelValue: string =
        objItem[fieldAtLabel as keyof ObjectAcceptType] ?? '';
      return {
        label: getTranslatedLabel(labelValue, translationPath),
        value: hasIdKey ? objItem.id || '' : labelValue,
      };
    });
  }
  //Whether the array is key-value pair array
  if (isKeyValueArray(array)) {
    return (array as [string, string][]).map(([key, value]) => {
      const translatedLabel = getTranslatedLabel(
        allowedKeyEntryAsValue ? value : key,
        translationPath,
      );

      return {
        label: translatedLabel,
        value: allowedKeyEntryAsValue ? key : value,
      };
    });
  }

  return [];
};
